import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Skeleton,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  Autorenew as AutorenewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useFleetCriteria } from '../hooks/useFleetSubscriptions';

interface FleetCriteriaCardProps {
  onUpdateCriteria: () => void;
}

const FleetCriteriaCard: React.FC<FleetCriteriaCardProps> = ({ onUpdateCriteria }) => {
  const { data: criteria, isLoading } = useFleetCriteria();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" height={200} />
        </CardContent>
      </Card>
    );
  }

  if (!criteria) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" color="text.secondary" align="center">
            No fleet criteria configuration found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const lastUpdated = new Date(criteria.last_updated);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Fleet Manager Qualification Criteria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the requirements for fleet manager status and auto-downgrade settings
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={onUpdateCriteria}
          >
            Update Criteria
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Minimum Vehicles */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <CarIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Minimum Vehicles Required
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {criteria.min_vehicles}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Hosts with {criteria.min_vehicles}+ vehicles automatically qualify as Fleet Managers
              </Typography>
            </Paper>
          </Grid>

          {/* Auto-Downgrade */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '2px solid',
                borderColor: criteria.auto_downgrade_enabled ? 'success.main' : 'error.main',
                borderRadius: 2,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: criteria.auto_downgrade_enabled
                      ? 'success.main'
                      : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  {criteria.auto_downgrade_enabled ? <CheckCircleIcon /> : <CancelIcon />}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Auto-Downgrade
                  </Typography>
                  <Chip
                    label={criteria.auto_downgrade_enabled ? 'Enabled' : 'Disabled'}
                    color={criteria.auto_downgrade_enabled ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {criteria.auto_downgrade_enabled
                  ? 'Fleet Managers are automatically downgraded when they fall below requirements'
                  : 'Manual intervention required to downgrade fleet managers'}
              </Typography>
            </Paper>
          </Grid>

          {/* Verification Period */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ScheduleIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Verification Period
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {criteria.verification_period_days} days
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Grace Period */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AutorenewIcon sx={{ color: '#9333ea', fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Grace Period (before downgrade)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {criteria.grace_period_days} days
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* How It Works */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              How Fleet Manager Qualification Works
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                Hosts with <strong>{criteria.min_vehicles}+ active vehicles</strong> automatically
                qualify as Fleet Managers
              </li>
              <li>
                Hosts with <strong>active premium subscription</strong> also qualify as Fleet Managers
              </li>
              <li>
                When a Fleet Manager drops below {criteria.min_vehicles} vehicles AND has no active
                subscription:
                <ul style={{ paddingLeft: 20 }}>
                  <li>
                    They enter a <strong>{criteria.grace_period_days}-day grace period</strong>
                  </li>
                  <li>
                    After the grace period, they are{' '}
                    {criteria.auto_downgrade_enabled ? (
                      <strong>automatically downgraded to Individual</strong>
                    ) : (
                      <span>flagged for manual review</span>
                    )}
                  </li>
                </ul>
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Audit Info */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Last Updated
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {lastUpdated.toLocaleString()} {criteria.updated_by && `by Admin #${criteria.updated_by}`}
          </Typography>
          {criteria.update_reason && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Reason
              </Typography>
              <Typography variant="body2">{criteria.update_reason}</Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FleetCriteriaCard;
